import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_NAME = "PalletRackingUSA";
const SITE_ORIGIN = "https://palletrackingusa.com";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/og-image.png`;

interface PageMeta {
  title: string;
  description: string;
  ogImage?: string;
}

function setMetaTag(property: string, content: string, isName = false) {
  const attr = isName ? "name" : "property";
  let el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

/**
 * Sets document title, meta description, and OG/Twitter card tags for the current page.
 */
export function usePageMeta({ title, description, ogImage }: PageMeta) {
  const { pathname } = useLocation();

  useEffect(() => {
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
    const clean = pathname === "/" ? "/" : pathname.replace(/\/+$/, "");
    const canonicalUrl = `${SITE_ORIGIN}${clean}`;
    const image = ogImage || DEFAULT_OG_IMAGE;

    // Title
    document.title = fullTitle;

    // Description
    setMetaTag("description", description, true);

    // OpenGraph
    setMetaTag("og:title", fullTitle);
    setMetaTag("og:description", description);
    setMetaTag("og:url", canonicalUrl);
    setMetaTag("og:image", image);
    setMetaTag("og:type", "website");
    setMetaTag("og:site_name", SITE_NAME);

    // Twitter Card
    setMetaTag("twitter:card", "summary_large_image", true);
    setMetaTag("twitter:title", fullTitle, true);
    setMetaTag("twitter:description", description, true);
    setMetaTag("twitter:image", image, true);
  }, [title, description, ogImage, pathname]);
}
